import {
  Box,
  Button,
  Card,
  Divider,
  FormHelperText,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { request } from '../../../../api/axios.ts';
import type {
  ScreeningTracingIndividualDetailsDto,
  ScreeningTracingReferencePersonDto,
  UpdateScreeningTracingIndividualDto,
} from '../../../../api/created/screening.ts';
import {
  getGetScreeningTracingIndividualQueryKey,
  getScreeningTracingIndividual,
  useUpdateScreeningTracingIndividual,
} from '../../../../api/created/screening.ts';
import type { ReferencePersonDto } from '../../../../api/generated/model/bezugspersonDto.ts';
import { checkPersonDuplicate } from '../../../../api/generated/person/checkDuplicate.ts';
import { SyncListAction } from '../../../../components/SyncListAction.tsx';
import { FieldMetaInfos, useAutoSaveForm } from '../../../../hooks/useAutoSaveForm.ts';
import {
  DuplicateCheckField,
  DuplicateErrors,
  PersonDataFormFields,
  PersonDataFormValues,
  getDuplicateCheckPayload,
  getDuplicateErrors,
} from '../../../masterdata/components/PersonDataFormFields.tsx';

export function ScreeningIndividualPersonalDetailsCard({
  individual,
}: {
  individual: ScreeningTracingIndividualDetailsDto;
}) {
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const individualQueryKey = getGetScreeningTracingIndividualQueryKey(individual.id);
  const updateScreeningIndividualMutation = useUpdateScreeningTracingIndividual({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(individualQueryKey, data);
      },
      onError: () =>
        toast.error('Bei dem Speichern der Personendaten ist ein Fehler aufgetreten', { toastId: 'saveError' }),
    },
  });
  const syncPersonMutation = useMutation({
    mutationFn: () =>
      request({
        url: `/api/patients/${individual.id}/sync-person`,
        method: 'POST',
      }),
    onSuccess: async () => {
      const updatedIndividual = await queryClient.fetchQuery({
        queryKey: individualQueryKey,
        queryFn: ({ signal }) => getScreeningTracingIndividual(individual.id, undefined, signal),
      });

      resetToSourceData(updatedIndividual);
      setSyncDialogOpen(false);
      toast.success('Personeninformationen wurden erfolgreich synchronisiert');
    },
    onError: () => {
      toast.error('Beim Synchronisieren der Personeninformationen ist ein Fehler aufgetreten');
    },
  });

  const {
    register,
    control,
    setValue,
    getValues,
    getFieldState,
    resetToSourceData,
    formState: { errors },
  } = useAutoSaveForm({
    sourceData: individual,
    getDefaultValues,
    save: (data) =>
      updateScreeningIndividualMutation.mutateAsync({
        id: individual.id,
        data: mapFormValuesToPayload(data),
      }),
    fieldMetaInfos,
    validationMode: 'onChange',
    onValidationFailed: () =>
      toast.warn('Personendaten können aufgrund eines Fehlers momentan nicht gespeichert werden', {
        toastId: 'validationError',
        autoClose: false,
      }),
    onValidationPassed: () => toast.dismiss('validationError'),
  });
  const [duplicateErrors, setDuplicateErrors] = useState<DuplicateErrors>({});
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);

  const checkDuplicateField = async (field: DuplicateCheckField, value: string | null | undefined) => {
    const payload = getDuplicateCheckPayload(field, value, getValues());

    if (Object.keys(payload).length === 0) {
      setDuplicateErrors({});
      return;
    }

    try {
      setIsCheckingDuplicate(true);

      const response = await checkPersonDuplicate(payload);
      setDuplicateErrors(getDuplicateErrors(payload, response, individual.personId));
    } catch (error) {
      console.error('Duplicate check failed', error);
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  return (
    <Card>
      <Box display="flex" alignItems="center" gap={1} justifyContent="space-between">
        <Typography level="title-lg">Personalien</Typography>
        <SyncListAction
          loading={syncPersonMutation.isPending}
          onClick={() => setSyncDialogOpen(true)}
          title="Person synchronisieren"
        />
      </Box>
      <Divider />
      <PersonDataFormFields
        register={register}
        control={control}
        setValue={setValue}
        getValues={getValues}
        getFieldState={getFieldState}
        errors={errors}
        duplicateErrors={duplicateErrors}
        checkDuplicateField={checkDuplicateField}
        referencePersonSelect={{
          mode: 'edit',
          personId: individual.id,
          createEndpoint: 'patient',
          fetchedOptions: mapReferencePersons(individual.bezugspersonen ?? []),
        }}
      />
      {isCheckingDuplicate && <FormHelperText>Duplikate werden geprüft...</FormHelperText>}
      <Modal open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Synchronisierung von Personeninformationen</Typography>
          <Typography>Möchten Sie wirklich die Personeninformationen synchronisieren?</Typography>
          <Stack direction="row" gap={2} justifyContent="flex-end" mt={1}>
            <Button variant="outlined" color="neutral" onClick={() => setSyncDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button loading={syncPersonMutation.isPending} onClick={() => syncPersonMutation.mutate()}>
              Bestätigen
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Card>
  );
}

function getDefaultValues(individual: ScreeningTracingIndividualDetailsDto): PersonDataFormValues {
  return {
    firstName: individual.firstName ?? '',
    lastName: individual.lastName ?? '',
    birthDate: individual.birthDate ?? '',
    gender: individual.gender ?? null,
    mobilePhoneNumber: individual.mobilePhoneNumber || individual.phone || '',
    privatePhoneNumber: individual.privatePhoneNumber ?? '',
    workPhoneNumber: individual.workPhoneNumber ?? '',
    email: individual.email ?? '',
    nationality: individual.nationality ?? null,
    origin: individual.origin ?? null,
    arrival: individual.arrival ?? '',
    asylumNumber: individual.asylumNumber ?? '',
    zemisNumber: individual.zemisNumber ?? '',
    languages: individual.languages ?? [],
    street: individual.street ?? '',
    additionalInformation: individual.additionalInformation ?? '',
    bezugspersonen: mapReferencePersons(individual.bezugspersonen ?? []),
    residencyPermit: individual.residencyPermit ?? null,
    zip: individual.postalCode ? String(individual.postalCode) : '',
    city: individual.city ?? '',
    canton: individual.state ?? '',
    address: null,
    contact: null,
    salutation: individual.salutation ?? '',
    livingStatus: individual.livingStatus ?? null,
    note: individual.note ?? '',
  };
}

function mapFormValuesToPayload(data: PersonDataFormValues): UpdateScreeningTracingIndividualDto {
  return {
    firstName: data.firstName.trim(),
    lastName: data.lastName.trim(),
    birthDate: data.birthDate,
    gender: data.gender,
    salutation: getStringOrNull(data.salutation),
    livingStatus: data.livingStatus ?? null,
    note: getStringOrNull(data.note),
    email: getStringOrNull(data.email),
    mobilePhoneNumber: getStringOrNull(data.mobilePhoneNumber),
    privatePhoneNumber: getStringOrNull(data.privatePhoneNumber),
    workPhoneNumber: getStringOrNull(data.workPhoneNumber),
    nationality: data.nationality,
    origin: data.origin,
    arrival: getStringOrNull(data.arrival),
    asylumNumber: getStringOrNull(data.asylumNumber),
    zemisNumber: getStringOrNull(data.zemisNumber),
    residencyPermit: data.residencyPermit,
    street: getStringOrNull(data.street),
    postalCode: getStringOrNull(data.zip),
    city: getStringOrNull(data.city),
    state: getStringOrNull(data.canton),
    additionalInformation: getStringOrNull(data.additionalInformation),
    languages: data.languages ?? [],
    bezugspersonIds: (data.bezugspersonen ?? [])
      .filter((item) => item.isSelected && item.id)
      .map((item) => item.id as string),
  };
}

function getStringOrNull(value: string | null | undefined) {
  const trimmedValue = value?.trim();

  return trimmedValue || null;
}

function mapReferencePersons(referencePersons: ScreeningTracingReferencePersonDto[]): ReferencePersonDto[] {
  return referencePersons.map(
    ({ id, relationType, salutation, firstName, lastName, position, phoneNumber, email, isSelected }) =>
      ({
        id,
        relationType,
        salutation,
        firstName,
        lastName,
        position,
        phoneNumber,
        email,
        isSelected,
      }) as ReferencePersonDto,
  );
}

const fieldMetaInfos: FieldMetaInfos<PersonDataFormValues> = {
  firstName: { debounce: true },
  lastName: { debounce: true },
  birthDate: { debounce: true },
  gender: { debounce: false },
  salutation: { debounce: false },
  livingStatus: { debounce: false },
  note: { debounce: true },
  mobilePhoneNumber: { debounce: true },
  privatePhoneNumber: { debounce: true },
  workPhoneNumber: { debounce: true },
  email: { debounce: true },
  nationality: { debounce: false },
  origin: { debounce: false },
  arrival: { debounce: true },
  asylumNumber: { debounce: true },
  zemisNumber: { debounce: true },
  languages: { debounce: false },
  residencyPermit: { debounce: false },
  zip: { debounce: true },
  city: { debounce: true },
  canton: { debounce: true },
  street: { debounce: true },
  additionalInformation: { debounce: true },
  address: { debounce: true },
  contact: { debounce: true },
  bezugspersonen: { debounce: false, ignorePropertiesForComparison: ['id', 'isSelected', 'salutation'] },
};
